import { FC } from "react"
import clsx from "clsx"

import { useTimezone } from "hooks/useTimezone"

import { TagRuleRun } from "services/tags/type"

function statusBadge(status: string) {
  const base = "badge badge-sm"
  if (status === "SUCCESS") return `${base} badge-success`
  if (status === "FAILED") return `${base} badge-error`
  if (status === "RUNNING") return `${base} badge-warning`
  return base
}

export const RunsTable: FC<{
  rows: TagRuleRun[]
  loading: boolean
}> = ({ rows, loading }) => {
  const { formatDateTime } = useTimezone()

  function fmt(dt?: string | null) {
    if (!dt) return "-"
    try {
      return formatDateTime(dt)
    } catch {
      return String(dt)
    }
  }
  function getRunSourceLabel(run: TagRuleRun) {
    if (run.mode === "RULE") return "Manual (rule)"
    return run.triggered_by_user_id ? "Manual (batch)" : "Cron (batch)"
  }

  return (
    <div className="mt-4 overflow-x-auto">
      <table className="table w-full text-black dark:text-white">
        <thead className="text-black/70 dark:text-white/80">
          <tr>
            <th>Started</th>
            <th>Mode</th>
            <th>Source</th>
            <th>Status</th>
            <th>Rule</th>
            <th>Tag</th>
            <th>Processed</th>
            <th>Assigned</th>
            <th>Removed</th>
            <th>Done</th>
            <th>Error</th>
            <th>Finished</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={12}>
                <div className="flex items-center gap-2">
                  <span className="loading loading-spinner loading-md" />
                  <span>Loading...</span>
                </div>
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={12} className="opacity-70">
                No runs found
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id} className="hover:bg-black/5 dark:hover:bg-white/5">
                <td className="opacity-80">{fmt(r.started_at)}</td>
                <td>{r.mode}</td>
                <td>{getRunSourceLabel(r)}</td>
                <td>
                  <span className={statusBadge(r.status)}>{r.status}</span>
                </td>
                <td className="font-medium">{r.rule?.name ?? (r.rule_id ? r.rule_id : "— (batch)")}</td>
                <td className="opacity-80">{r.rule?.tag?.name ?? "-"}</td>
                <td>{r.processed ?? "-"}</td>
                <td>{r.assigned ?? "-"}</td>
                <td>{r.removed ?? "-"}</td>
                <td>{typeof r.done === "boolean" ? (r.done ? "Yes" : "No") : "-"}</td>
                <td className={clsx("max-w-[360px] truncate", r.status === "FAILED" ? "text-error" : "opacity-60")} title={r.error ?? ""}>
                  {r.error ?? "-"}
                </td>
                <td className="opacity-80">{fmt(r.finished_at)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
